import { useRef, useState, useCallback, useEffect } from "react"
import Strings from "utils/strings"
import { fleetAuthTokenFetcher, clearTokenCache } from "utils/fleetAuth"
import {
	takenPolylineCustomization,
	activePolylineCustomization,
	getStopMarkerColor,
} from "utils/fleetMapConfig"
import { mapStyles } from "./styles"
import { getColorForVehicle } from "./utils"
import { FleetVehicle, FleetTask, VehicleJourneySegment } from "./types"
import { createInfoWindowContent } from "components/FleetTrackingMap/utils"

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
const FLEET_ENGINE_PROJECT_ID = process.env.REACT_APP_FLEET_ENGINE_PROJECT_ID

const LISBON_CENTER = { lat: 38.72152, lng: -9.142161 }

declare const google: any

interface UseFleetTrackingOptions {
	businessId?: string | null
	onVehicleClick?: (vehicleId: string) => void
	onFleetUpdate?: (vehicles: FleetVehicle[]) => void
}

export function useFleetTracking({
	businessId,
	onVehicleClick,
	onFleetUpdate,
}: UseFleetTrackingOptions) {
	const mapContainerRef = useRef<HTMLDivElement>(null)
	const mapViewRef = useRef<any>(null)
	const infoWindowRef = useRef<any>(null)
	const fleetLocationProviderRef = useRef<any>(null)
	const vehicleLocationProviderRef = useRef<any>(null)
	const isInitializedRef = useRef(false)
	const prevBusinessIdRef = useRef<string | null | undefined>(undefined)

	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [showPOIs, setShowPOIs] = useState(false)
	const [vehicles, setVehicles] = useState<FleetVehicle[]>([])
	const [selectedVehicle, setSelectedVehicle] = useState<FleetVehicle | null>(
		null
	)
	const [vehicleTasks, setVehicleTasks] = useState<VehicleJourneySegment[]>([])

	const handleTogglePOIs = useCallback(() => {
		if (!mapViewRef.current?.map) return

		const newValue = !showPOIs
		setShowPOIs(newValue)

		mapViewRef.current.map.setOptions({
			styles: newValue ? [] : mapStyles.hidePOIs,
			clickableIcons: newValue,
		})
	}, [showPOIs])

	const loadJourneySharingLibrary = useCallback(async (): Promise<void> => {
		if (
			typeof google !== "undefined" &&
			google.maps?.journeySharing?.FleetEngineDeliveryFleetLocationProvider
		) {
			return
		}

		if (typeof google !== "undefined" && google.maps?.importLibrary) {
			await google.maps.importLibrary("journeySharing")
			return
		}

		return new Promise((resolve, reject) => {
			const existingScript = document.querySelector(
				'script[src*="maps.googleapis.com"]'
			)
			if (existingScript) {
				const checkLoaded = setInterval(() => {
					if (typeof google !== "undefined" && google.maps?.importLibrary) {
						clearInterval(checkLoaded)
						google.maps
							.importLibrary("journeySharing")
							.then(resolve)
							.catch(reject)
					}
				}, 100)
				setTimeout(() => {
					clearInterval(checkLoaded)
					reject(new Error("Timeout waiting for Google Maps"))
				}, 10000)
				return
			}

			const script = document.createElement("script")
			script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=journeySharing`
			script.async = true
			script.defer = true

			script.onload = () => {
				if (
					google.maps?.journeySharing?.FleetEngineDeliveryFleetLocationProvider
				) {
					resolve()
				} else {
					reject(new Error("Journey Sharing library not available"))
				}
			}

			script.onerror = () =>
				reject(new Error("Failed to load Google Maps script"))

			document.head.appendChild(script)
		})
	}, [])

	const updateFleetStats = useCallback(
		(vehicles: FleetVehicle[]) => {
			if (onFleetUpdate) {
				onFleetUpdate(vehicles)
			}
		},
		[onFleetUpdate]
	)

	useEffect(() => {
		if (!isInitializedRef.current || prevBusinessIdRef.current === businessId) {
			return
		}

		prevBusinessIdRef.current = businessId

		if (fleetLocationProviderRef.current) {
			const filter = businessId
				? `attributes.businessId = "${businessId}"`
				: null
			fleetLocationProviderRef.current.deliveryVehicleFilter = filter
		}
	}, [businessId])

	useEffect(() => {
		const initializeFleetTracking = async () => {
			if (!mapContainerRef.current || isInitializedRef.current) {
				return
			}

			try {
				setIsLoading(true)
				setError(null)

				await loadJourneySharingLibrary()

				const deliveryVehicleFilter = businessId
					? `attributes.businessId = "${businessId}"`
					: null

				const today = new Date()
				const startOfDay = new Date(
					today.getFullYear(),
					today.getMonth(),
					today.getDate()
				)
				const endOfDay = new Date(
					today.getFullYear(),
					today.getMonth(),
					today.getDate(),
					23,
					59,
					59
				)
				const fleetLocationProvider =
					new google.maps.journeySharing.FleetEngineDeliveryFleetLocationProvider(
						{
							projectId: FLEET_ENGINE_PROJECT_ID,
							authTokenFetcher: fleetAuthTokenFetcher,
							deliveryVehicleFilter,
							deliveryVehicleMarkerCustomization: (params: any) => {
								if (params.isNew) {
									let title =
										params.vehicle.attributes?.licensePlate ||
										params.vehicle.name.split("/").pop()
									const vehicleId = params.vehicle.name.split("/").pop()
									const markerColor = getColorForVehicle(vehicleId)

									const currentIcon = params.marker.getIcon()
									params.marker.setTitle(title)
									params.marker.setLabel({
										text: title,
										color: "black",
										fontSize: "14px",
										fontWeight: "700",
										fontFamily: "Arial, sans-serif",
									})
									params.marker.setIcon({
										...(typeof currentIcon === "object" ? currentIcon : {}),
										fillColor: markerColor,
										fillOpacity: 1,
									})

									params.marker.addListener("click", () => {
										const vehicleId = params.vehicle.name.split("/").pop()

										const vehicle: FleetVehicle = {
											name: params.vehicle.name,
											vehicleId,
											licensePlate:
												params.vehicle.attributes?.licensePlate || vehicleId,
											currentLocation: params.vehicle
												.latestVehicleLocationUpdate
												? {
														lat: params.vehicle.latestVehicleLocationUpdate
															.location.lat,
														lng: params.vehicle.latestVehicleLocationUpdate
															.location.lng,
													}
												: undefined,
											remainingDistanceMeters:
												params.vehicle.remainingDistanceMeters,
											remainingDurationMillis:
												params.vehicle.remainingDurationMillis,
											remainingVehicleJourneySegments:
												params.vehicle.remainingVehicleJourneySegments,
											attributes: params.vehicle.attributes,
											lastUpdate: params.vehicle.latestVehicleLocationUpdate
												?.time
												? new Date(
														params.vehicle.latestVehicleLocationUpdate.time
													)
												: undefined,
											speed:
												params.vehicle.latestVehicleLocationUpdate
													?.speedKilometersPerHour,
										}
										setSelectedVehicle(vehicle)
										setVehicleTasks([])

										if (vehicleLocationProviderRef.current) {
											vehicleLocationProviderRef.current.deliveryVehicleId =
												vehicleId
										}

										if (onVehicleClick) {
											onVehicleClick(vehicleId)
										}
									})
								}
							},
						}
					)

				fleetLocationProviderRef.current = fleetLocationProvider
				prevBusinessIdRef.current = businessId

				fleetLocationProvider.addListener("update", (e: any) => {
					if (e.deliveryVehicles) {
						const todayStr = today.toISOString().slice(0, 10)
						const vehicles: FleetVehicle[] = e.deliveryVehicles
							.filter((v: any) => {
								const t = v.latestVehicleLocationUpdate?.time
								if (!t) return false
								return new Date(t).toISOString().startsWith(todayStr)
							})
							.map((v: any) => ({
								name: v.name,
								vehicleId: v.name.split("/").pop(),
								licensePlate: v.attributes?.licensePlate,
								currentLocation: v.latestVehicleLocationUpdate
									? {
											lat: v.latestVehicleLocationUpdate.location.lat,
											lng: v.latestVehicleLocationUpdate.location.lng,
										}
									: undefined,
								remainingDistanceMeters: v.remainingDistanceMeters,
								remainingVehicleJourneySegments:
									v.remainingVehicleJourneySegments,
								remainingDurationMillis: v.remainingDurationMillis,
								attributes: v.attributes,
								lastUpdate: v.latestVehicleLocationUpdate?.time
									? new Date(v.latestVehicleLocationUpdate.time)
									: undefined,
								speed: v.latestVehicleLocationUpdate?.speedKilometersPerHour,
							}))
						setVehicles(vehicles)
						updateFleetStats(vehicles)
					}
				})

				fleetLocationProvider.addListener("error", (e: any) => {
					console.error("Fleet Engine location provider error:", e)
					clearTokenCache()
					setTimeout(() => {
						const bounds = mapViewRef.current?.map?.getBounds()
						if (bounds) {
							fleetLocationProvider.locationRestriction = bounds
						}
					}, 2000)
				})

				const vehicleLocationProvider =
					new google.maps.journeySharing.FleetEngineDeliveryVehicleLocationProvider(
						{
							projectId: FLEET_ENGINE_PROJECT_ID,
							authTokenFetcher: fleetAuthTokenFetcher,
							shouldShowTasks: true,
							shouldShowOutcomeLocations: true,
							taskFilterOptions: {
								completionTimeFrom: startOfDay,
								completionTimeTo: endOfDay,
							},
							takenPolylineCustomization,
							activePolylineCustomization,
							plannedStopMarkerCustomization: (params: any) => {
								const segment =
									params.vehicle?.remainingVehicleJourneySegments?.[
										params.stopIndex
									]
								const state = segment?.stop?.state
								if (state) {
									const color = getStopMarkerColor(state)
									if (color) {
										const currentIcon = params.marker.getIcon()
										params.marker.setIcon({
											...(typeof currentIcon === "object" ? currentIcon : {}),
											fillColor: color,
											fillOpacity: 1,
										})
									}
								}
							},
							taskMarkerCustomization: (params: any) => {
								params.marker.setTitle(params.task?.trackingId)
								params.marker.addListener("click", () => {
									if (infoWindowRef.current) {
										infoWindowRef.current.close()
									}

									const trackingId = params.task?.trackingId || params.task?.id
									const infoWindow = new google.maps.InfoWindow({
										content: createInfoWindowContent(
											trackingId ? [trackingId] : []
										),
									})

									infoWindowRef.current = infoWindow
									infoWindow.open(mapViewRef.current?.map, params.marker)
								})
							},
							taskOutcomeMarkerCustomization: (params: any) => {
								params.marker.setTitle(params.task?.trackingId)
							},
							deliveryVehicleMarkerCustomization: (params: any) => {
								if (params.isNew) {
									const origSetVisible = params.marker.setVisible.bind(
										params.marker
									)
									params.marker.setVisible = () => origSetVisible(false)
								}
								params.marker.setVisible(false)
							},
						}
					)

				vehicleLocationProviderRef.current = vehicleLocationProvider

				vehicleLocationProvider.addListener("update", (e: any) => {
					if (e.deliveryVehicle) {
						setSelectedVehicle((prev) => {
							if (!prev) return prev
							return {
								...prev,
								remainingDistanceMeters:
									e.deliveryVehicle.remainingDistanceMeters,
								remainingDurationMillis:
									e.deliveryVehicle.remainingDurationMillis,
								remainingVehicleJourneySegments:
									e.deliveryVehicle.remainingVehicleJourneySegments,
								lastUpdate: e.deliveryVehicle.latestVehicleLocationUpdate?.time
									? new Date(e.deliveryVehicle.latestVehicleLocationUpdate.time)
									: prev.lastUpdate,
								speed:
									e.deliveryVehicle.latestVehicleLocationUpdate
										?.speedKilometersPerHour,
							}
						})
						if (e.deliveryVehicle.remainingVehicleJourneySegments) {
							setVehicleTasks(e.deliveryVehicle.remainingVehicleJourneySegments)
						}
					}
				})

				const mapView = new google.maps.journeySharing.JourneySharingMapView({
					element: mapContainerRef.current,
					locationProviders: [fleetLocationProvider, vehicleLocationProvider],
					mapOptions: {
						center: LISBON_CENTER,
						zoom: 11,
						mapTypeControl: true,
						fullscreenControl: true,
						streetViewControl: false,
						clickableIcons: false,
						styles: mapStyles.hidePOIs,
					},
				})

				mapViewRef.current = mapView
				isInitializedRef.current = true

				google.maps.event.addListener(mapView.map, "bounds_changed", () => {
					const bounds = mapView.map.getBounds()
					if (bounds) {
						fleetLocationProvider.locationRestriction = bounds
					}
				})

				setIsLoading(false)
			} catch (err) {
				console.error("Fleet tracking initialization error:", err)
				setError(
					err instanceof Error
						? err.message
						: Strings.fleetDashboard.errorLoadingFleet
				)
				setIsLoading(false)
			}
		}

		initializeFleetTracking()

		return () => {
			if (fleetLocationProviderRef.current) {
				fleetLocationProviderRef.current.locationRestriction = null
			}
			if (vehicleLocationProviderRef.current) {
				vehicleLocationProviderRef.current.deliveryVehicleId = ""
			}
		}
	}, [loadJourneySharingLibrary, updateFleetStats, onVehicleClick])

	const handleCloseVehiclePanel = () => {
		setSelectedVehicle(null)
		setVehicleTasks([])
		if (vehicleLocationProviderRef.current) {
			vehicleLocationProviderRef.current.deliveryVehicleId = ""
		}
	}

	return {
		mapContainerRef,
		isLoading,
		error,
		vehicles,
		selectedVehicle,
		vehicleTasks,
		showPOIs,
		handleTogglePOIs,
		handleCloseVehiclePanel,
	}
}
